import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CompanyExperiment,
  ExperimentFlag,
  GetFeatureExperimentsResponse,
  SpecialUser,
  UserExperiment
} from '@blisspointmedia/bpm-types/dist/FeatureExperiments';
import { CompanyID, GetUserAdminResponse, User } from '@blisspointmedia/bpm-types/dist/UserAdmin';
import {
  SaveChangesInput,
  SelectorOption
} from '../../Components/ModalEditWidget/modal-edit-constants';
import { useSetError } from '../../redux/modals';
import { awaitJSON, UsersLambdaFetch } from '../../utils/fetch-utils';
import { GroupedDataRow } from './types';
import { getSpecialUsersByGroup, groupData, makeRowId, ungroupData } from './utils';

type JsonName = 'user_experiments' | 'company_experiments' | 'special_users';

export const useGroupedExperimentTable = <
  T extends UserExperiment | CompanyExperiment | SpecialUser,
  K extends keyof T
>(
  jsonName: JsonName,
  expectedKeys: (keyof T & string)[],
  groupByColumn: keyof T,
  groupedColumn: K,
  createNewItem: (groupKey: string, groupedItem: string) => T
) => {
  type FrontendRow = GroupedDataRow<T, K>;

  const [initialTableData, setInitialTableData] = useState<FrontendRow[]>([]);
  const [originalGroupedDataMap, setOriginalGroupedDataMap] = useState<Record<string, FrontendRow>>(
    {}
  );
  const [originalExperimentMap, setOriginalExperimentMap] = useState<
    Record<string, T & { id: string }>
  >({});
  const [allExperimentFlags, setAllExperimentFlags] = useState<ExperimentFlag[]>([]);
  const [userEmails, setUserEmails] = useState<string[]>([]);
  const [companies, setCompanies] = useState<string[]>([]);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [specialUsers, setSpecialUsers] = useState<SpecialUser[]>([]);
  const [loading, setLoading] = useState(true);

  const setError = useSetError();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [featureData, userData] = await Promise.all([
        UsersLambdaFetch('/getFeatureExperiments').then(res =>
          awaitJSON<GetFeatureExperimentsResponse>(res)
        ),
        UsersLambdaFetch('/userAdmin/getUsers').then(res => awaitJSON<GetUserAdminResponse>(res))
      ]);

      const rawList = featureData[jsonName];
      if (!Array.isArray(rawList)) {
        throw new Error(
          `Expected an array for featureExperimentData[${String(jsonName)}], got: ${typeof rawList}`
        );
      }
      rawList.forEach((item: object, i: number) => {
        for (const key of expectedKeys) {
          if (!(key in item)) {
            throw new Error(
              `Item at index ${i} in featureExperimentData[${String(jsonName)}] is missing expected key "${key}"`
            );
          }
        }
      });
      const experiments = rawList as T[];
      const allUsers: User[] = userData.users;
      const allCompanies: CompanyID[] = userData.companies;

      const flatMap: Record<string, T & { id: string }> = {};
      const rowsWithId: (T & { id: string })[] = experiments.map(row => {
        const id = makeRowId([String(row[groupByColumn]), String(row[groupedColumn])]);
        const rowWithId = { ...row, id };
        flatMap[id] = rowWithId;
        return rowWithId;
      });

      const grouped: FrontendRow[] = groupData<T, K>(rowsWithId, groupByColumn, groupedColumn).map(
        (row, idx) => ({ ...row, id: String(idx) })
      );
      const groupedMap: Record<string, FrontendRow> = {};
      for (const row of grouped) groupedMap[row.id] = row;

      setInitialTableData(grouped);
      setOriginalGroupedDataMap(groupedMap);
      setOriginalExperimentMap(flatMap);
      setAllExperimentFlags(featureData.experiment_flags);
      setUserEmails(allUsers.map(u => u.email));
      setCompanies(allCompanies.map(c => c.cid));
      setSpecialUsers(featureData.special_users);
      setUserGroups(Array.from(new Set(featureData.special_users.map(s => s.user_group))));
    } catch (e) {
      setError({
        message: `Error fetching feature experiment data: ${e.message}`,
        reportError: e
      });
    } finally {
      setLoading(false);
    }
  }, [jsonName, groupByColumn, groupedColumn, expectedKeys, setError]);

  const onSaveModal = useCallback(
    async ({ addedData, modifiedData, deletedData, cleanup }: SaveChangesInput<FrontendRow>) => {
      try {
        const newExperimentsFlat = ungroupData<T, K>(
          addedData,
          groupByColumn,
          groupedColumn,
          originalExperimentMap,
          createNewItem
        );
        const deletedExperimentsFlat = ungroupData<T, K>(
          deletedData,
          groupByColumn,
          groupedColumn,
          originalExperimentMap,
          createNewItem
        );

        const editedInserts: T[] = [];
        const editedDeletes: T[] = [];

        modifiedData.forEach(row => {
          const original = originalGroupedDataMap[row.id];
          if (!original) return;

          const currentValues = row[groupedColumn].map(String);
          const originalValues = original[groupedColumn].map(String);

          const groupKeyChanged =
            row[groupByColumn as keyof FrontendRow] !==
            original[groupByColumn as keyof FrontendRow];
          const added = groupKeyChanged
            ? currentValues
            : currentValues.filter(v => !originalValues.includes(v));
          const removed = groupKeyChanged
            ? originalValues
            : originalValues.filter(v => !currentValues.includes(v));

          added.forEach(value => {
            editedInserts.push(
              createNewItem(String(row[groupByColumn as keyof FrontendRow]), value)
            );
          });

          removed.forEach(value => {
            const key = makeRowId([String(row[groupByColumn as keyof FrontendRow]), value]);
            const originalFlat = originalExperimentMap[key];
            let experiment = createNewItem(
              String(original[groupByColumn as keyof FrontendRow]),
              value
            );
            if (originalFlat && 'lastmodified' in originalFlat && 'lastuser' in originalFlat) {
              experiment = {
                ...experiment,
                [groupByColumn]: originalFlat[groupByColumn],
                lastmodified: originalFlat.lastmodified,
                lastuser: originalFlat.lastuser
              };
            }
            editedDeletes.push(experiment);
          });
        });

        const allInserts = [...newExperimentsFlat, ...editedInserts].flatMap((row): T[] => {
          const groupedColumnValue = String(row[groupedColumn]);
          if (!groupedColumnValue.startsWith('group::')) return [row];

          const groupName = groupedColumnValue.slice('group::'.length);
          const expanded = getSpecialUsersByGroup(groupName, specialUsers).map(user => ({
            ...row,
            user_email: user.user
          }));

          return expanded.filter(specialUserRow => {
            const key = makeRowId([
              String(specialUserRow[groupByColumn]),
              String(specialUserRow[groupedColumn])
            ]);
            if (
              originalExperimentMap[key] &&
              !editedDeletes.some(
                r =>
                  r[groupByColumn] === specialUserRow[groupByColumn] &&
                  r[groupedColumn] === specialUserRow[groupedColumn]
              )
            ) {
              return false;
            }
            const existsInNew = newExperimentsFlat.some(
              r =>
                r[groupByColumn] === specialUserRow[groupByColumn] &&
                r[groupedColumn] === specialUserRow[groupedColumn]
            );
            const existsInEdited = editedInserts.some(
              r =>
                r[groupByColumn] === specialUserRow[groupByColumn] &&
                r[groupedColumn] === specialUserRow[groupedColumn]
            );
            return !existsInNew && !existsInEdited;
          });
        });

        const allDeletes = [...deletedExperimentsFlat, ...editedDeletes];

        await UsersLambdaFetch('/updateFeatureExperiments', {
          method: 'POST',
          body: { [jsonName]: { insert: allInserts, delete: allDeletes } }
        });
        cleanup();
        await refresh();
      } catch (e) {
        setError({
          message: `Failed to save feature experiment data: ${e.message}`,
          reportError: e
        });
      }
    },
    [
      groupByColumn,
      groupedColumn,
      originalExperimentMap,
      originalGroupedDataMap,
      createNewItem,
      specialUsers,
      jsonName,
      refresh,
      setError
    ]
  );

  const selectorOptions = useMemo<Record<string, SelectorOption[]>>(() => {
    const existingFlags = new Set<string>(
      initialTableData.map(row => String(row[groupByColumn as keyof FrontendRow]))
    );
    const filteredExperimentFlags = allExperimentFlags.filter(
      flag => !existingFlags.has(flag.experiment_flag)
    );

    return {
      experiment_flag: filteredExperimentFlags.map(flag => ({
        label: flag.experiment_flag,
        value: flag.experiment_flag
      })),
      user_email: [
        ...userGroups.map(group => ({ label: `Group: ${group}`, value: `group::${group}` })),
        ...userEmails.map(email => ({ label: email, value: email }))
      ],
      company: companies.map(c => ({ label: c, value: c })),
      special_user: userEmails
        .filter(email =>
          ['@blisspointmedia.com', '@tinuiti.com'].some(domain => email.endsWith(domain))
        )
        .map(email => ({ label: email, value: email }))
    };
  }, [allExperimentFlags, initialTableData, userEmails, userGroups, companies, groupByColumn]);

  useEffect(() => {
    const init = async () => {
      await refresh();
    };
    init();
  }, [refresh]);

  return { initialTableData, selectorOptions, onSaveModal, loading };
};
